/* eslint-disable boundaries/element-types */
import React, { useEffect, useMemo } from 'react'
import { SelectPicker } from 'rsuite'
import { ItemDataType } from 'rsuite/esm/MultiCascadeTree'
import styled, { createGlobalStyle } from 'styled-components'
import { DropdownOption } from 'types'

import {
  InputAndSubtitle,
  StyledInputContainer,
  StyledLabel,
  SubtitleRowStyled,
  SubtitleTextStyled,
} from './InputStyles'

export interface DropdownProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  $itemKey?: string
  $label?: string
  $labelColor?: string
  $labelLocation?: 'top' | 'side'
  $subtitleText?: string
  $subtitleTextColor?: string
  $backgroundColor?: string
  $borderColor?: string
  units?: string
  options: DropdownOption[]
  onValueChange: (value: string) => void
  onClean?: () => void
  defaultValue?: string
  placeholder?: string
  mode?: 'read' | 'edit'
  searchable?: boolean
  cleanable?: boolean
  disabled?: boolean
  renderMenuItem?: (
    label: React.ReactNode,
    item: ItemDataType
  ) => React.ReactNode
}

const SelectPickerStyle = createGlobalStyle<{
  $backgroundColor?: string
  $borderColor?: string
}>`
  .rs-picker {
    width: 100%;
  }

  .rs-picker-default .rs-picker-toggle.rs-btn {
    padding: 0.25rem;
    background-color: ${({ $backgroundColor, theme }) =>
      $backgroundColor || theme.forecast.cardGroupBackground} !important;
    border-color: ${({ $borderColor }) => $borderColor || 'transparent'} !important;
    border-radius: 0.25rem;
  }

  .rs-btn {
    &:hover {
      background-color: ${({ $backgroundColor, theme }) =>
        $backgroundColor || theme.forecast.hoverGhostButton} !important;
    }
  }

  .rs-picker-toggle-value {
    padding-left: 0.25rem;
  }

  /* Added placeholder style */
  .rs-picker-toggle-placeholder {
    color: #888 !important;
    padding-left: 0.25rem;
    font-size: 12px !important;
  }
`

const ReadOnlyDiv = styled.div`
  display: flex;
  align-items: center;
  padding: var(--Padding-1_5, 6px) var(--Padding-1, 4px);
  color: ${({ theme }) => theme.forecast.defaultText};
`

export const Dropdown = ({
  options,
  onValueChange,
  defaultValue = '',
  onClean,
  $label,
  $itemKey,
  $labelColor,
  $labelLocation,
  $subtitleText,
  $subtitleTextColor,
  $backgroundColor,
  $borderColor,
  mode,
  cleanable = true,
  placeholder,
  searchable = true,
  disabled,
  renderMenuItem,
}: DropdownProps) => {
  const dropdownContainerRef = React.useRef<HTMLDivElement>(null)
  const [selectedValue, setSelectedValue] = React.useState<string | undefined>(
    defaultValue
  )
  const toggleOption = (toggledOption: string) => {
    const filteredOption = options?.find(
      (option) => option.value === toggledOption
    )
    if (filteredOption !== undefined) {
      setSelectedValue(filteredOption.value)
      onValueChange(toggledOption)
    }
  }

  const renderReadOnlyValue = useMemo(() => {
    const filteredOption = options?.find(
      (option) => option.value === defaultValue
    )
    return filteredOption?.label
  }, [defaultValue, options])

  useEffect(() => {
    if (mode === 'read') {
      const filteredOption = options?.find(
        (option) => option.value === defaultValue
      )
      if (filteredOption !== undefined) {
        setSelectedValue(filteredOption.value)
      }
    }
  }, [mode, options, defaultValue, setSelectedValue])

  const removeOption = () => {
    setSelectedValue(undefined)
    onValueChange('')
  }

  return (
    <StyledInputContainer $labelLocation={$labelLocation}>
      {$label && (
        <StyledLabel $labelColor={$labelColor} htmlFor={$label}>
          {$label}
        </StyledLabel>
      )}

      <div
        ref={dropdownContainerRef}
        style={{ position: 'relative', width: '100%' }}>
        <InputAndSubtitle>
          {mode === 'read' ? (
            <ReadOnlyDiv>{renderReadOnlyValue}</ReadOnlyDiv>
          ) : (
            <>
              <SelectPickerStyle
                $backgroundColor={$backgroundColor}
                $borderColor={$borderColor}
              />
              <SelectPicker
                cleanable={cleanable}
                data-testid={`custom-form-field-${$label}`}
                id={$itemKey}
                aria-label={$itemKey}
                aria-labelledby={$itemKey}
                name={$itemKey}
                onChange={(e) => {
                  if (e) {
                    toggleOption(e)
                  }
                }}
                onClean={onClean ?? removeOption}
                searchable={searchable}
                value={selectedValue}
                data={options ?? []}
                aria-required='true'
                placeholder={placeholder}
                container={dropdownContainerRef.current ?? undefined}
                disabled={disabled}
                renderMenuItem={renderMenuItem}
                virtualized={options.length > 50}
              />
            </>
          )}
          {mode !== 'read' && $subtitleText && (
            <SubtitleRowStyled>
              <SubtitleTextStyled $subtitleTextColor={$subtitleTextColor}>
                {$subtitleText}
              </SubtitleTextStyled>
            </SubtitleRowStyled>
          )}
        </InputAndSubtitle>
      </div>
    </StyledInputContainer>
  )
}

Dropdown.displayName = 'Dropdown'
